import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, Search, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LevelIndicator = ({ level }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${
            i <= level ? 'bg-sky-400' : 'bg-slate-200'
          }`}
        />
      ))}
    </div>
  );
};

const PlayersPage = () => {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        withCredentials: true
      });
      setPlayers(response.data);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlayers = players.filter(player => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      player.name?.toLowerCase().includes(searchLower) ||
      player.surname?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-32 bg-slate-100 rounded-3xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="players-page">
      {/* Header */}
      <div>
        <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-slate-900">
          Giocatori
        </h1>
        <p className="text-slate-500 mt-1">
          {players.length} membri del circolo
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Cerca giocatore..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="search-players"
          className="w-full pl-12 pr-4 py-3 rounded-2xl border border-slate-200 bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 transition-all outline-none"
        />
      </div>

      {/* Players Grid */}
      {filteredPlayers.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-10 h-10 text-slate-400" />
          </div>
          <h3 className="font-outfit text-xl font-medium text-slate-900 mb-2">
            Nessun giocatore trovato
          </h3>
          <p className="text-slate-500">
            Prova a cercare con un altro nome
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPlayers.map((player, index) => (
            <Link
              key={player.user_id}
              to={`/players/${player.user_id}`}
              data-testid={`player-card-${index}`}
              className="bg-white rounded-3xl p-6 border border-slate-100 hover:border-sky-200 hover:-translate-y-1 hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-sky-400 rounded-2xl flex items-center justify-center text-white text-xl font-semibold overflow-hidden">
                  {getAvatarUrl(player) ? (
                    <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-2xl object-cover" />
                  ) : (
                    player.name?.charAt(0)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-outfit font-semibold text-slate-900 truncate">
                    {player.name} {player.surname || ''}
                  </p>
                  {player.age && (
                    <p className="text-sm text-slate-500">{player.age} anni</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 mb-1">Livello</p>
                  <LevelIndicator level={player.level || 1} />
                </div>
                {player.user_id !== user?.user_id && (
                  <div className="flex items-center gap-2 text-sky-500 text-sm">
                    <MessageCircle className="w-4 h-4" />
                    Messaggio
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlayersPage;
